import React, { useEffect, useRef, useState } from "react";
import LoginModal from "./LoginModal";
import { useAuth } from "../shared/auth";
import { deliverSession, type PopupProvider } from "../shared/auth-frame";

// /auth/popup: the sign-in window the game opens when it runs inside another
// site's frame (shared/auth-frame.ts). ?relay= is the frame's code; ?p= starts
// that provider at once. Once signed in it hands the session over and closes.
// A magic link opens this page in a new tab, which hands over the same way.

const params = new URLSearchParams(window.location.search);
const CODE = /^[0-9a-f]{64}$/.test(params.get("relay") || "") ? params.get("relay")! : null;
const PROVIDER = ["google", "discord", "facebook"].includes(params.get("p") || "") ? (params.get("p") as PopupProvider) : null;

export default function AuthPopup() {
  const { session, loading, signInWithGoogle, signInWithDiscord, signInWithFacebook } = useAuth();
  const [state, setState] = useState<"signing" | "done" | "failed">("signing");
  const started = useRef(false);
  const delivered = useRef(false);

  // ?p=: straight on to the provider (back here signed in, the code kept in the URL)
  useEffect(() => {
    if (loading || session || !CODE || !PROVIDER || started.current) return;
    if (window.location.hash.includes("access_token")) return;
    started.current = true;
    // (drop ?p= first: the provider sends us back to this address, and a
    // cancelled sign-in must not start over by itself)
    const u = new URL(window.location.href);
    u.searchParams.delete("p");
    window.history.replaceState(null, "", u.toString());
    const go = { google: signInWithGoogle, discord: signInWithDiscord, facebook: signInWithFacebook }[PROVIDER];
    void go();
  }, [loading, session]);

  useEffect(() => {
    if (!session || !CODE || delivered.current) return;
    delivered.current = true;
    void deliverSession(CODE, session).then((ok) => {
      setState(ok ? "done" : "failed");
      if (ok) window.setTimeout(() => window.close(), 900);
    });
  }, [session]);

  let body: React.ReactNode;
  if (!CODE) body = <p>Nothing to sign in to here.</p>;
  else if (session) body = state === "failed"
    ? <p>Couldn't reach the game. Close this window and try again.</p>
    : <p>{state === "done" ? "Signed in. Back to the game." : "Signing in…"}</p>;
  else if (PROVIDER || loading) body = <p>Signing in…</p>;
  else return <LoginModal onClose={() => window.close()} />;

  return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, textAlign: "center", color: "#e9f3ff", font: "600 16px/1.4 Outfit, sans-serif" }}>{body}</div>;
}
